import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import Dropdown from 'reactstrap/lib/Dropdown';
import DropdownToggle from 'reactstrap/lib/DropdownToggle';
import DropdownMenu from 'reactstrap/lib/DropdownMenu';
import DropdownItem from 'reactstrap/lib/DropdownItem';
import { getQuery } from 'client/utils/location';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ReviewsModel, ReviewEntities, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { DEALER_EVENTS } from 'site-modules/dealerships/utils/dealer-events';
import { EventToolbox } from 'client/utils/event-toolbox';
import {
  DEALER_PROPS,
  DEALER_REVIEWS_PER_PAGE,
  REVIEWS_ALL,
  SORT_RATING,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
  REVIEWS_PER_CLICK,
  DEFAULT_REVIEWS_TYPE,
  SORT_UPDATED,
} from 'client/constants/dealer-reviews';
import { DealerReviewsPerPage } from 'site-modules/dealerships/components/dealer-reviews-per-page/dealer-reviews-per-page';

import './dealer-review-list.scss';

function isReferredUrlFromEdmunds() {
  if (typeof document !== 'undefined') {
    return document.referrer.indexOf('edmunds.com') !== -1;
  }
  return false;
}

function getVisibleReviewsNum({ seeReviews, reviewsType }) {
  return seeReviews || reviewsType !== REVIEWS_ALL ? REVIEWS_PER_CLICK : 0;
}

class DealerReviewsListUI extends Component {
  constructor(props) {
    super(props);
    const query = getQuery(this.props.location);
    this.state = {
      isSortDropdownOpen: false,
      sortBy: SORT_UPDATED,
      order: SORT_ORDER_DESC,
      sortLabel: 'Newest',
      numVisible: getVisibleReviewsNum({
        seeReviews: query.seeReviews === 'true',
        reviewsType: this.props.reviewsType,
      }),
    };
  }

  componentDidMount() {
    const query = getQuery(this.props.location);
    if (query.referrer === 'true' || isReferredUrlFromEdmunds()) {
      EventToolbox.fireCustomEvent(DEALER_EVENTS.INTERNAL_REFERRER);
      this.setInitialReviewCount(); // we want reviews expanded if user came from an Edmunds page.
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.reviewsType !== this.props.reviewsType) {
      this.setExpandedReviews();
    }
  }

  setExpandedReviews = () => {
    const query = getQuery(this.props.location);
    this.setState({
      numVisible: getVisibleReviewsNum({
        seeReviews: query.seeReviews === 'true',
        reviewsType: this.props.reviewsType,
      }),
    });
  };

  setInitialReviewCount = () => {
    this.setState({ numVisible: REVIEWS_PER_CLICK });
  };

  toggleSortDropdown = () => {
    this.setState(prevState => ({ isSortDropdownOpen: !prevState.isSortDropdownOpen }));
  };

  sortReviews = (sortBy, order, sortLabel) => () => {
    if (this.props.reviewsFilter.sortBy !== sortBy || this.props.reviewsFilter.order !== order) {
      this.props.setModelValue(ReviewsPaths.getReviewsFilterPath(), ReviewsModel, {
        sortBy,
        order,
        page: '1',
      });
    }
    this.setState({ sortBy, order, sortLabel });
  };

  readMoreReviews = event => {
    if (this.state.numVisible < DEALER_REVIEWS_PER_PAGE) {
      event.preventDefault();
      this.setState(prevState => ({ numVisible: prevState.numVisible + REVIEWS_PER_CLICK }));
    } else {
      // otherwise this will behave as a link - instead we probably want to make this an AJAX request
    }
  };

  closeAllReviews = () => {
    this.setState({ numVisible: 0 });
  };

  render() {
    const {
      params,
      reviewsType,
      totalReviews,
      reviewsFilter: { page, type },
      isMobile,
    } = this.props;
    const { numVisible, isSortDropdownOpen, sortLabel } = this.state;
    const pageNumber = parseInt(page, 10);
    const reviewsCollapsed = numVisible === 0;
    const hasAnotherPage = totalReviews > page * DEALER_REVIEWS_PER_PAGE;
    const nextPageLink = hasAnotherPage
      ? `?page=${pageNumber + 1}${type && type !== DEFAULT_REVIEWS_TYPE ? `&type=${type}` : ''}`
      : '#';
    const showReadMoreButton =
      hasAnotherPage ||
      (numVisible < DEALER_REVIEWS_PER_PAGE && DEALER_REVIEWS_PER_PAGE * (pageNumber - 1) + numVisible < totalReviews);

    return (
      <div>
        <div className="dealer-review-list">
          <div className={classNames({ 'd-none': reviewsCollapsed })}>
            <div className="sort-review-wrapper d-flex justify-content-between">
              <span className="size-14 text-cool-gray-30">
                {totalReviews} {reviewsType === REVIEWS_ALL ? '' : reviewsType} reviews
              </span>
              <div className={classNames('text-end', { 'd-none': totalReviews < 2 })}>
                <span className="size-14 text-cool-gray-30 me-0_25">Sort by:</span>
                <Dropdown
                  isOpen={isSortDropdownOpen}
                  toggle={this.toggleSortDropdown}
                  className="d-inline-block size-14 text-cool-gray-30"
                >
                  <DropdownToggle
                    caret
                    color="text-cool-gray-30"
                    className="fw-bold d-inline-block pe-1_5 ps-0 pt-0 pb-0 size-14 text-cool-gray-30 bg-none text-transform-none"
                  >
                    {sortLabel}
                  </DropdownToggle>
                  <DropdownMenu right>
                    <DropdownItem onClick={this.sortReviews(SORT_UPDATED, SORT_ORDER_DESC, 'Newest')}>
                      Newest
                    </DropdownItem>
                    <DropdownItem onClick={this.sortReviews(SORT_RATING, SORT_ORDER_DESC, 'Highest')}>
                      Highest
                    </DropdownItem>
                    <DropdownItem onClick={this.sortReviews(SORT_RATING, SORT_ORDER_ASC, 'Lowest')}>
                      Lowest
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
            <hr className="mb-0" />
            <DealerReviewsPerPage type={reviewsType} params={params} numVisible={numVisible} isMobile={isMobile} />
            <div className="text-end mt-1">
              <button
                className="btn btn-link px-0 py-0 text-transform-none mb-0 fw-normal size-14 close-all-review-link"
                onClick={this.closeAllReviews}
              >
                See less
              </button>
              <hr className="my-0" />
            </div>
          </div>
          <div className="text-center my-2">
            <a
              className={classNames(
                'read-review-link btn btn-blue-50 rounded-8 my-0 mx-auto text-transform-none fw-medium size-16',
                {
                  'd-none': !showReadMoreButton,
                  'w-100': isMobile,
                }
              )}
              href={nextPageLink}
              onClick={this.readMoreReviews}
              data-tracking-id="ddp_view_dealer_reviews"
              data-tracking-value={`read ${reviewsType} reviews`}
            >
              {numVisible < DEALER_REVIEWS_PER_PAGE ? 'Show More Reviews' : 'Next Page'}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

DealerReviewsListUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  reviewsType: PropTypes.string.isRequired,
  reviewsFilter: ReviewEntities.ReviewsFilter,
  totalReviews: PropTypes.number.isRequired,
  params: DEALER_PROPS.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
};

DealerReviewsListUI.defaultProps = {
  location: {},
  reviewsFilter: {
    type: null,
    page: '0',
    sortBy: SORT_UPDATED,
    order: SORT_ORDER_DESC,
  },
  isMobile: false,
};

export const DealerReviewsList = withRouter(
  connectToModel(DealerReviewsListUI, {
    reviewsFilter: bindToPath(ReviewsPaths.getReviewsFilterPath, ReviewsModel),
  })
);
